import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button";
import { DialogDescription, DialogTitle } from "@/modern/components/ui/dialog";
import { getBryntumFloatRootElement } from "@/other/utils/domUtils";

const Lightbox = DialogPrimitive.Root;

const LightboxTrigger = DialogPrimitive.Trigger;

const LightboxPortal = ({ children }: { children: React.ReactNode }) => {
  const container = getBryntumFloatRootElement();
  return <DialogPrimitive.Portal container={container}>
    {children}
  </DialogPrimitive.Portal>
};

const LightboxClose = DialogPrimitive.Close;

const LightboxOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));

LightboxOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface LightboxImage {
  src: string;
  alt?: string;
  caption?: string;
}

interface LightboxContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  images: LightboxImage[];
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  showThumbnails?: boolean;
  allowZoom?: boolean;
  isOpen: boolean;
}

const LightboxContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  LightboxContentProps
>(
  (
    {
      className,
      children,
      images,
      initialIndex = 0,
      onIndexChange,
      showThumbnails = false,
      allowZoom = true,
      isOpen,
      ...props
    },
    ref
  ) => {
    const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [imagePosition, setImagePosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const imageRef = React.useRef<HTMLImageElement>(null);

    const currentImage = images[currentIndex];

    const handlePrevious = React.useCallback(() => {
      const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      setCurrentIndex(newIndex);
      onIndexChange?.(newIndex);
      setIsZoomed(false);
      setImagePosition({ x: 0, y: 0 });
    }, [currentIndex, images.length, onIndexChange]);

    const handleNext = React.useCallback(() => {
      const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      setCurrentIndex(newIndex);
      onIndexChange?.(newIndex);
      setIsZoomed(false);
      setImagePosition({ x: 0, y: 0 });
    }, [currentIndex, images.length, onIndexChange]);

    const handleZoomToggle = React.useCallback(() => {
      if (allowZoom) {
        setIsZoomed(!isZoomed);
        if (isZoomed) {
          setImagePosition({ x: 0, y: 0 });
        }
      }
    }, [isZoomed, allowZoom]);

    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent) => {
        if (isZoomed) {
          setIsDragging(true);
          setDragStart({
            x: e.clientX - imagePosition.x,
            y: e.clientY - imagePosition.y
          });
        }
      },
      [isZoomed, imagePosition]
    );

    const handleMouseMove = React.useCallback(
      (e: React.MouseEvent) => {
        if (isDragging && isZoomed) {
          setImagePosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
          });
        }
      },
      [isDragging, isZoomed, dragStart]
    );

    const handleMouseUp = React.useCallback(() => {
      setIsDragging(false);
    }, []);

    // Keyboard navigation
    React.useEffect(() => {
      if (isOpen) {
        const handleKeyDown = (e: KeyboardEvent) => {
          switch (e.key) {
            case "ArrowLeft":
              e.preventDefault();
              handlePrevious();
              break;
            case "ArrowRight":
              e.preventDefault();
              handleNext();
              break;
            case "Escape":
              // Let the dialog handle escape
              break;
            case " ":
              e.preventDefault();
              handleZoomToggle();
              break;
          }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }

      return;
    }, [handlePrevious, handleNext, handleZoomToggle]);

    // Update index when initialIndex changes
    React.useEffect(() => {
      setCurrentIndex(initialIndex);
      setIsZoomed(false);
      setImagePosition({ x: 0, y: 0 });
    }, [initialIndex]);

    const handleThumbnailClick = (index: number) => {
      setCurrentIndex(index);
      onIndexChange?.(index);
      setIsZoomed(false);
      setImagePosition({ x: 0, y: 0 });
    };

    if (!currentImage) return null;

    return (
      <LightboxPortal>
        <DialogTitle className="sr-only">Lightbox</DialogTitle>
        <DialogDescription className="sr-only">Lightbox</DialogDescription>
        <LightboxOverlay className="z-[--swivu-bryntum-z-index]" />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "fixed left-0 top-0 z-[--swivu-bryntum-z-index] flex h-full w-full items-center justify-center focus:outline-none",
            className
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
          {...props}
        >
          {/* Close button */}
          <DialogPrimitive.Close asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 z-10 h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70 focus:ring-2 focus:ring-white/50"
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>

          {/* Navigation buttons */}
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 focus:ring-2 focus:ring-white/50"
                onClick={handlePrevious}
              >
                <ChevronLeft className="h-8 w-8" />
                <span className="sr-only">Previous image</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70 focus:ring-2 focus:ring-white/50"
                onClick={handleNext}
              >
                <ChevronRight className="h-8 w-8" />
                <span className="sr-only">Next image</span>
              </Button>
            </>
          )}

          {/* Zoom controls */}
          {allowZoom && (
            <div className="absolute left-4 bottom-4 z-10 flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70 focus:ring-2 focus:ring-white/50"
                onClick={handleZoomToggle}
              >
                {isZoomed ? (
                  <ZoomOut className="h-5 w-5" />
                ) : (
                  <ZoomIn className="h-5 w-5" />
                )}
                <span className="sr-only">
                  {isZoomed ? "Zoom out" : "Zoom in"}
                </span>
              </Button>
            </div>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute right-4 bottom-4 z-10 rounded-full bg-black/50 px-3 py-1 text-sm text-white">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* Main image container */}
          <div
            className="flex h-full w-full items-center justify-center p-4"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              ref={imageRef}
              src={currentImage.src}
              alt={currentImage.alt || `Image ${currentIndex + 1}`}
              className={cn(
                "max-h-full max-w-full object-contain transition-transform duration-200",
                isZoomed && "cursor-move scale-150",
                !isZoomed && allowZoom && "cursor-zoom-in",
                isDragging && "cursor-grabbing"
              )}
              style={
                isZoomed
                  ? {
                      transform: `scale(1.5) translate(${
                        imagePosition.x / 1.5
                      }px, ${imagePosition.y / 1.5}px)`
                    }
                  : undefined
              }
              onClick={allowZoom ? handleZoomToggle : undefined}
              onMouseDown={handleMouseDown}
              draggable={false}
            />
          </div>

          {/* Thumbnails */}
          {showThumbnails && images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2 rounded-lg bg-black/70 p-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={cn(
                    "h-12 w-12 overflow-hidden rounded border-2 transition-all",
                    index === currentIndex
                      ? "border-white"
                      : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img
                    src={image.src}
                    alt={image.alt || `Thumbnail ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Caption */}
          {currentImage.caption && (
            <div className="absolute top-4 left-1/2 z-10 max-w-md -translate-x-1/2 rounded-lg bg-black/70 px-4 py-2 text-left text-sm text-white">
              {currentImage.caption}
            </div>
          )}

          {children}
        </DialogPrimitive.Content>
      </LightboxPortal>
    );
  }
);
LightboxContent.displayName = DialogPrimitive.Content.displayName;

// Context for managing lightbox state across multiple triggers
interface LightboxContextValue {
  images: LightboxImage[];
  isOpen: boolean;
  currentIndex: number;
  openLightbox: (index: number) => void;
  closeLightbox: () => void;
  showThumbnails: boolean;
  allowZoom: boolean;
}

const LightboxContext = React.createContext<LightboxContextValue | null>(null);

function useLightboxContext() {
  const context = React.useContext(LightboxContext);
  if (!context) {
    throw new Error(
      "useLightboxContext must be used within a LightboxProvider"
    );
  }
  return context;
}

// Provider component that manages state for multiple triggers
interface LightboxProviderProps {
  children: React.ReactNode;
  images: (LightboxImage | File)[];
  showThumbnails?: boolean;
  allowZoom?: boolean;
  onIndexChange?: (index: number) => void;
}

const LightboxProvider = ({
  children,
  images: rawImages,
  showThumbnails = false,
  allowZoom = true,
  onIndexChange
}: LightboxProviderProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [imageUrls, setImageUrls] = React.useState<Map<File, string>>(
    new Map()
  );

  // Convert mixed array to LightboxImage array
  const images = React.useMemo(() => {
    return rawImages
      .map((item) => {
        if (item instanceof File) {
          const url = imageUrls.get(item);
          return {
            src: url || "",
            alt: item.name,
            caption: `${item.name} (${(item.size / 1024 / 1024).toFixed(2)}MB)`
          };
        }
        return item as LightboxImage;
      })
      .filter((img) => img.src); // Only include images with valid URLs
  }, [rawImages, imageUrls]);

  // Manage File object URLs
  React.useEffect(() => {
    const filesToProcess = rawImages.filter(
      (item): item is File => item instanceof File
    );
    setImageUrls((prevUrls) => {
      const newUrls = new Map<File, string>();

      // First, preserve existing URLs for files that are still in the array
      filesToProcess.forEach((file) => {
        const existingUrl = prevUrls.get(file);
        if (existingUrl) {
          newUrls.set(file, existingUrl);
        } else {
          // Only create new URL if we don't have one
          const url = URL.createObjectURL(file);
          newUrls.set(file, url);
        }
      });

      // Clean up URLs for files that are no longer in the current array
      prevUrls.forEach((url, file) => {
        if (!filesToProcess.includes(file)) {
          URL.revokeObjectURL(url);
        }
      });

      return newUrls;
    });
  }, [rawImages]);

  // Cleanup all URLs on unmount
  React.useEffect(() => {
    return () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const openLightbox = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < images.length) {
        setCurrentIndex(index);
        setIsOpen(true);
        onIndexChange?.(index);
      }
    },
    [images.length, onIndexChange]
  );

  const closeLightbox = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleIndexChange = React.useCallback(
    (index: number) => {
      setCurrentIndex(index);
      onIndexChange?.(index);
    },
    [onIndexChange]
  );

  const contextValue: LightboxContextValue = {
    images,
    isOpen,
    currentIndex,
    openLightbox,
    closeLightbox,
    showThumbnails,
    allowZoom
  };

  return (
    <LightboxContext.Provider value={contextValue}>
      {children}
      <Lightbox open={isOpen} onOpenChange={setIsOpen}>
        <LightboxContent
          images={images}
          initialIndex={currentIndex}
          onIndexChange={handleIndexChange}
          showThumbnails={showThumbnails}
          allowZoom={allowZoom}
          isOpen={isOpen}
        />
      </Lightbox>
    </LightboxContext.Provider>
  );
};

interface LightboxImageTriggerProps {
  index: number;
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const LightboxImageTrigger = ({
  index,
  children,
  asChild = false,
  className
}: LightboxImageTriggerProps) => {
  const { openLightbox } = useLightboxContext();

  const handleClick = React.useCallback(() => {
    openLightbox(index);
  }, [index, openLightbox]);

  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      onClick={handleClick}
      className={cn(!asChild && "cursor-pointer", className)}
    >
      {children}
    </Comp>
  );
};

export {
  Lightbox,
  LightboxTrigger,
  LightboxContent,
  LightboxClose,
  LightboxProvider,
  LightboxImageTrigger,
  useLightboxContext
};
